import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PatientModuleDataResponse } from '@features/patient-context-bar';
import { useLazyGetPatientModuleDataQuery } from '@features/patient-context-bar';
import { useSnackbar } from 'notistack';

import { useMergePatientGroupMutation } from '../store';
import type { PatientSummary } from '../types';
import type {
  MergeCustomizationLocationState,
  ModuleItemSelection,
  NormalizedModuleItem,
  NormalizedModuleMap,
  SelectionKey,
} from '../types/merge-customization';
import { makeSelectionKey, parseSelectionKey } from '../types/merge-customization';
import { isIdField, isTechnicalValue } from '../utils/fieldFilters';

// ---------------------------------------------------------------------------
// Module normalization helpers
// ---------------------------------------------------------------------------

const MODULE_SUFFIXES = ['_ptmodule', '_testmodule'];

/**
 * Derive a canonical module name from the module key or API name field.
 * e.g. "diagnoses_ptmodule" → "diagnoses"
 *      "autism_spectrum_disorder_screening_testmodule" → "autism_spectrum_disorder_screening"
 */
function deriveCanonicalName(moduleKey: string, apiName?: string): string {
  if (apiName) return apiName.toLowerCase();
  let name = moduleKey.toLowerCase();
  for (const suffix of MODULE_SUFFIXES) {
    if (name.endsWith(suffix)) {
      name = name.slice(0, -suffix.length);
      break;
    }
  }
  return name;
}

/** Title-case a snake_case module name for display. */
function formatDisplayName(canonicalName: string): string {
  return canonicalName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Schema-driven display label
// ---------------------------------------------------------------------------

interface SchemaField {
  name: string;
  description: string;
  required: boolean;
  type: string; // 'bool' | 'date' | 'number' | string (text)
}

/**
 * Build a human-readable display label for a module item.
 *
 * Strategy (mirrors TableBlock's schema-driven approach):
 * 1. If a schema is provided, use it to pick the best display-worthy fields
 *    — skip bools, dates, and ID fields; prefer string fields first.
 * 2. If no schema, inspect the data keys directly with the same heuristics.
 */
function getDisplayLabel(
  data: Record<string, unknown>,
  schema: SchemaField[] | null,
): string {
  if (schema && schema.length > 0) {
    return getDisplayLabelFromSchema(data, schema);
  }
  return getDisplayLabelFromData(data);
}

/** Pick up to 2 display-worthy field values guided by the schema. */
function getDisplayLabelFromSchema(
  data: Record<string, unknown>,
  schema: SchemaField[],
): string {
  // Partition schema fields into preferred (text) vs secondary (number)
  const textFields: SchemaField[] = [];
  const numberFields: SchemaField[] = [];

  for (const field of schema) {
    if (isIdField(field.name)) continue;
    if (field.type === 'bool' || field.type === 'date') continue;
    if (field.type === 'number') {
      numberFields.push(field);
    } else {
      textFields.push(field);
    }
  }

  const parts: string[] = [];

  // Try text fields first
  for (const field of textFields) {
    const val = data[field.name];
    if (typeof val === 'string' && val && !isTechnicalValue(val)) {
      parts.push(val);
    }
    if (parts.length >= 2) break;
  }

  // Fill with number fields if we still need more context
  if (parts.length < 2) {
    for (const field of numberFields) {
      const val = data[field.name];
      if (typeof val === 'number') {
        const label = formatDisplayName(field.name);
        parts.push(`${label}: ${val}`);
      }
      if (parts.length >= 2) break;
    }
  }

  return parts.join(' — ') || 'Item';
}

/** Fallback: inspect data keys directly when no schema is available. */
function getDisplayLabelFromData(data: Record<string, unknown>): string {
  const parts: string[] = [];

  for (const [key, val] of Object.entries(data)) {
    if (isIdField(key)) continue;
    if (typeof val === 'string' && val) {
      if (isTechnicalValue(val)) continue;
      parts.push(val);
    } else if (typeof val === 'number') {
      const label = formatDisplayName(key);
      parts.push(`${label}: ${val}`);
    }
    if (parts.length >= 2) break;
  }

  return parts.join(' — ') || 'Item';
}

/** Normalize a single patient's module data response into items. */
function normalizePatientModules(
  rawData: PatientModuleDataResponse | undefined,
  patientId: string,
  patientName: string,
  isPrimary: boolean,
): NormalizedModuleItem[] {
  const patientModulesData = rawData?.patient_data?.patient_module_data;
  if (!patientModulesData) return [];

  const items: NormalizedModuleItem[] = [];

  for (const [moduleKey, moduleData] of Object.entries(patientModulesData)) {
    const mod = moduleData as {
      name?: string;
      type?: string;
      current_data?: Record<string, unknown[]>;
      schema?: SchemaField[] | null;
    };
    const moduleName = deriveCanonicalName(moduleKey, mod.name);
    const schema = mod.schema ?? null;

    if (!mod.current_data) continue;

    for (const [dataKey, dataArray] of Object.entries(mod.current_data)) {
      if (!Array.isArray(dataArray)) continue;

      dataArray.forEach((rawItem, itemIndex) => {
        const data = rawItem as Record<string, unknown>;
        items.push({
          data,
          patientId,
          patientName,
          isPrimary,
          moduleKey,
          moduleName,
          dataKey,
          itemIndex,
          selectionKey: makeSelectionKey(patientId, moduleKey, itemIndex),
          displayLabel: getDisplayLabel(data, schema),
        });
      });
    }
  }

  return items;
}

/** Group flat items into a NormalizedModuleMap keyed by canonical module name. */
function buildModuleMap(allItems: NormalizedModuleItem[]): NormalizedModuleMap {
  const map: NormalizedModuleMap = {};

  for (const item of allItems) {
    const canonicalName = item.moduleName;

    if (!map[canonicalName]) {
      map[canonicalName] = {
        name: formatDisplayName(canonicalName),
        items: [],
      };
    }
    map[canonicalName].items.push(item);
  }

  return map;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface RemovedPatientRecord {
  patient: PatientSummary;
  wasPrimary: boolean;
}

export interface UseMergeCustomizationReturn {
  /** Normalized module data grouped by module name */
  modules: NormalizedModuleMap;
  /** Set of selected item keys */
  selections: Set<SelectionKey>;
  /** Toggle an individual item */
  toggleItem: (key: SelectionKey) => void;
  /** Select all items from a specific patient within a module */
  selectAllFromPatient: (moduleName: string, patientId: string) => void;
/** Select all items from a patient across all modules (additive) */
  selectAllItemsFromPatient: (patientId: string) => void;
  /** Deselect all items from a patient across all modules (subtractive) */
  deselectAllItemsFromPatient: (patientId: string) => void;
  /** Total count of selected items */
  selectedCount: number;
  /** Whether module data is still loading */
  isLoading: boolean;
  /** Whether the merge is in progress */
  isMerging: boolean;
  /** Errors from fetching module data (patientId → error message) */
  fetchErrors: Record<string, string>;
  /** Confirm and execute the merge */
  confirmMerge: () => Promise<void>;
  /** Patient color map for consistent chip colors */
  patientColors: Map<string, string>;

  // --- Primary patient management ---
  /** Currently selected primary patient ID */
  primaryPatientId: string;
  /** Change the primary patient */
  setPrimaryPatientId: (id: string) => void;
  /** Patients still active in the merge pool */
  activePatients: PatientSummary[];

  // --- Patient removal / undo ---
  /** Remove a patient from the merge pool */
  removePatient: (id: string) => void;
  /** Undo the most recent patient removal */
  undoRemovePatient: () => void;
  /** Whether removal is allowed (false when only 2 active patients remain) */
  canRemovePatient: boolean;
  /** Most recently removed patient info (for driving undo snackbar) */
  recentlyRemoved: RemovedPatientRecord | null;
}

const PATIENT_COLORS = [
  '#1976d2', // blue
  '#d32f2f', // red
  '#2e7d32', // green
  '#9c27b0', // purple
  '#ed6c02', // orange
];

/** Shuffle an array in-place using a seeded PRNG (Mulberry32). */
function seededShuffle<T>(arr: T[], seed: number): T[] {
  let s = seed | 0;
  for (let i = arr.length - 1; i > 0; i--) {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    const j = Math.floor(r * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Simple string → seed integer. */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return hash;
}

export function useMergeCustomization(
  locationState: MergeCustomizationLocationState,
): UseMergeCustomizationReturn {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { group, organizationId } = locationState;

  // --- Primary patient management ---
  const [primaryPatientId, setPrimaryPatientIdRaw] = useState<string>(
    locationState.primaryPatientId ?? group.patients[0]?.patient_id ?? '',
  );

  // --- Removed patients tracking ---
  const [removedPatientIds, setRemovedPatientIds] = useState<Set<string>>(new Set());
  const [recentlyRemoved, setRecentlyRemoved] = useState<RemovedPatientRecord | null>(null);

  const activePatients = useMemo(
    () => group.patients.filter(p => !removedPatientIds.has(p.patient_id)),
    [group.patients, removedPatientIds],
  );

  const activePatientIds = useMemo(
    () => new Set(activePatients.map(p => p.patient_id)),
    [activePatients],
  );

  const canRemovePatient = activePatients.length > 2;

  // --- Data fetching ---
  const [triggerFetch] = useLazyGetPatientModuleDataQuery();
  const [mergePatientGroup, { isLoading: isMerging }] = useMergePatientGroupMutation();

  const [rawResponses, setRawResponses] = useState<Record<string, PatientModuleDataResponse>>({});
  const [fetchErrors, setFetchErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const fetchedRef = useRef(false);

  // Fetch module data for all patients in parallel
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchAll = async () => {
      setIsLoading(true);
      const results: Record<string, PatientModuleDataResponse> = {};
      const errors: Record<string, string> = {};

      await Promise.all(
        group.patients.map(async patient => {
          try {
            const result = await triggerFetch({ patientId: patient.patient_id }).unwrap();
            results[patient.patient_id] = result;
          } catch (err) {
            errors[patient.patient_id] =
              err instanceof Error ? err.message : 'Failed to load module data';
          }
        }),
      );

      setRawResponses(results);
      setFetchErrors(errors);
      setIsLoading(false);
    };

    void fetchAll();
  }, [group.patients, triggerFetch]);

  // --- Patient color map ---
  const patientColors = useMemo(() => {
    // Shuffle palette with a seed from the group ID so the order
    // looks random but stays stable for the same group.
    const shuffled = seededShuffle([...PATIENT_COLORS], hashString(group.group_id));
    const colorMap = new Map<string, string>();
    group.patients.forEach((p, i) => {
      colorMap.set(p.patient_id, shuffled[i % shuffled.length]);
    });
    return colorMap;
  }, [group.patients, group.group_id]);

  // --- Normalize module data (filtered to active patients only) ---
  const modules = useMemo<NormalizedModuleMap>(() => {
    if (isLoading) return {};

    const allItems: NormalizedModuleItem[] = [];

    for (const patient of group.patients) {
      // Skip removed patients
      if (!activePatientIds.has(patient.patient_id)) continue;

      const raw = rawResponses[patient.patient_id];
      const name = [patient.first_name, patient.last_name].filter(Boolean).join(' ') || patient.patient_id.substring(0, 8);
      const items = normalizePatientModules(
        raw,
        patient.patient_id,
        name,
        patient.patient_id === primaryPatientId,
      );
      allItems.push(...items);
    }

    return buildModuleMap(allItems);
  }, [isLoading, rawResponses, group.patients, primaryPatientId, activePatientIds]);

  // --- Selection state ---
  const [selections, setSelections] = useState<Set<SelectionKey>>(new Set());
  const initializedRef = useRef(false);

  // Initialize selections: pre-check all primary patient items
  useEffect(() => {
    if (isLoading || initializedRef.current) return;
    initializedRef.current = true;

    const initial = new Set<SelectionKey>();
    for (const mod of Object.values(modules)) {
      for (const item of mod.items) {
        if (item.isPrimary) {
          initial.add(item.selectionKey);
        }
      }
    }
    setSelections(initial);
  }, [isLoading, modules]);

  // --- Re-initialize selections when primary changes ---
  const reinitSelectionsForPrimary = useCallback(
    (newPrimaryId: string) => {
      const newSelections = new Set<SelectionKey>();
      for (const mod of Object.values(modules)) {
        for (const item of mod.items) {
          if (item.patientId === newPrimaryId) {
            newSelections.add(item.selectionKey);
          }
        }
      }
      setSelections(newSelections);
    },
    [modules],
  );

  const setPrimaryPatientId = useCallback(
    (id: string) => {
      setPrimaryPatientIdRaw(id);
      // Re-init selections will happen reactively through modules memo update,
      // but we also eagerly set them here for immediate feedback
      reinitSelectionsForPrimary(id);
    },
    [reinitSelectionsForPrimary],
  );

  // --- Patient removal / undo ---
  const removePatient = useCallback(
    (patientId: string) => {
      if (!canRemovePatient) return;

      const patient = group.patients.find(p => p.patient_id === patientId);
      if (!patient) return;

      const wasPrimary = patientId === primaryPatientId;

      // Mark as removed
      setRemovedPatientIds(prev => new Set([...prev, patientId]));
      setRecentlyRemoved({ patient, wasPrimary });

      // Clear their selections
      setSelections(prev => {
        const next = new Set(prev);
        for (const key of prev) {
          const parsed = parseSelectionKey(key);
          if (parsed.patientId === patientId) {
            next.delete(key);
          }
        }
        return next;
      });

      // Auto-promote primary if this was the primary
      if (wasPrimary) {
        const remaining = group.patients.filter(
          p => p.patient_id !== patientId && !removedPatientIds.has(p.patient_id),
        );
        if (remaining.length > 0) {
          setPrimaryPatientIdRaw(remaining[0].patient_id);
        }
      }
    },
    [canRemovePatient, group.patients, primaryPatientId, removedPatientIds],
  );

  const undoRemovePatient = useCallback(() => {
    if (!recentlyRemoved) return;

    const { patient, wasPrimary } = recentlyRemoved;

    // Restore the patient
    setRemovedPatientIds(prev => {
      const next = new Set(prev);
      next.delete(patient.patient_id);
      return next;
    });

    // Restore primary if they were primary before removal
    if (wasPrimary) {
      setPrimaryPatientIdRaw(patient.patient_id);
    }

    setRecentlyRemoved(null);
  }, [recentlyRemoved]);

  const toggleItem = useCallback((key: SelectionKey) => {
    setSelections(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectAllFromPatient = useCallback(
    (moduleName: string, patientId: string) => {
      const mod = modules[moduleName];
      if (!mod) return;

      setSelections(prev => {
        const next = new Set(prev);
        // Remove all items from this module, then add only the target patient's
        for (const item of mod.items) {
          next.delete(item.selectionKey);
        }
        for (const item of mod.items) {
          if (item.patientId === patientId) {
            next.add(item.selectionKey);
          }
        }
        return next;
      });
    },
    [modules],
  );

  const selectAllItemsFromPatient = useCallback(
    (patientId: string) => {
      setSelections(prev => {
        const next = new Set(prev);
        for (const mod of Object.values(modules)) {
          for (const item of mod.items) {
            if (item.patientId === patientId) {
              next.add(item.selectionKey);
            }
          }
        }
        return next;
      });
    },
    [modules],
  );

  const deselectAllItemsFromPatient = useCallback(
    (patientId: string) => {
      setSelections(prev => {
        const next = new Set(prev);
        for (const mod of Object.values(modules)) {
          for (const item of mod.items) {
            if (item.patientId === patientId) {
              next.delete(item.selectionKey);
            }
          }
        }
        return next;
      });
    },
    [modules],
  );

  const selectedCount = selections.size;

  // --- Build merge payload ---
  const buildModuleSelections = useCallback((): ModuleItemSelection[] => {
    // Build a lookup from selectionKey → item for O(1) access
    const itemByKey = new Map(
      Object.values(modules).flatMap(mod => mod.items.map(item => [item.selectionKey, item] as const)),
    );

    const result: ModuleItemSelection[] = [];
    for (const key of selections) {
      const item = itemByKey.get(key);
      if (item) {
        result.push({
          module_name: item.moduleKey,
          source_patient_id: item.patientId,
          item_index: item.itemIndex,
        });
      }
    }
    return result;
  }, [selections, modules]);

  // --- Confirm merge ---
  const confirmMerge = useCallback(async () => {
    const secondaryPatientIds = activePatients
      .filter(p => p.patient_id !== primaryPatientId)
      .map(p => p.patient_id);

    try {
      const moduleSelections = buildModuleSelections();
      await mergePatientGroup({
        mergeGroupRequest: {
          organization_id: organizationId,
          patient_match_group: group.group_id,
          primary_patient_id: primaryPatientId,
          patient_ids_to_merge: secondaryPatientIds,
          module_selections: moduleSelections,
        },
      }).unwrap();

      enqueueSnackbar('Patients merged successfully', { variant: 'success' });
      navigate('/patients/merge');
    } catch {
      enqueueSnackbar('Failed to merge patients', { variant: 'error' });
    }
  }, [
    activePatients,
    primaryPatientId,
    organizationId,
    group.group_id,
    buildModuleSelections,
    mergePatientGroup,
    enqueueSnackbar,
    navigate,
  ]);

  return {
    modules,
    selections,
    toggleItem,
    selectAllFromPatient,
    selectAllItemsFromPatient,
    deselectAllItemsFromPatient,
    selectedCount,
    isLoading,
    isMerging,
    fetchErrors,
    confirmMerge,
    patientColors,
    primaryPatientId,
    setPrimaryPatientId,
    activePatients,
    removePatient,
    undoRemovePatient,
    canRemovePatient,
    recentlyRemoved,
  };
}
